import { useState, useRef } from "react";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Motion";
import { Camera, MapPin, Inbox, ImagePlus, X, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
    {
        icon: Camera,
        title: "Snap a Photo",
        body: "Take a clear, close-up picture of the fly. Try to capture it in bright, natural light against a neutral background (like your palm or a fly box foam pad).",
    },
    {
        icon: MapPin,
        title: "Tell Us the Water",
        body: "Let us know what river, lake, or general region you're fishing. Knowing the geography helps us match the insect hatch cycles accurately.",
    },
    {
        icon: Inbox,
        title: "Get Expert Analysis",
        body: "A real angler will review your photo and send a direct breakdown straight to your inbox showing you exactly how to rig and fish that pattern.",
    },
];

const compressImage = (file) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(
                (b) => {
                    URL.revokeObjectURL(url);
                    if (b) resolve(new File([b], "fly-photo.jpg", { type: "image/jpeg" }));
                    else reject(new Error("Could not process that image."));
                },
                "image/jpeg",
                0.85,
            );
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Could not read that image file."));
        };
        img.src = url;
    });

export default function IdentifyFly() {
    const [form, setForm] = useState({ name: "", email: "", water: "", details: "" });
    const [photo, setPhoto] = useState(null);
    const [preview, setPreview] = useState(null);
    const [status, setStatus] = useState("idle"); // idle | sending | done
    const [error, setError] = useState("");
    const fileRef = useRef(null);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const pickPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError("");
        try {
            const compressed = await compressImage(file);
            setPhoto(compressed);
            setPreview(URL.createObjectURL(compressed));
        } catch (err) {
            setError(err.message);
        }
    };

    const clearPhoto = () => {
        setPhoto(null);
        setPreview(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!photo) {
            setError("Please add a photo of the fly — that's the whole magic trick.");
            return;
        }
        setStatus("sending");
        setError("");
        try {
            const data = new FormData();
            data.append("name", form.name);
            data.append("email", form.email);
            data.append("water", form.water);
            data.append("details", form.details);
            data.append("photo", photo);
            const res = await fetch(`${API}/api/identify-fly`, { method: "POST", body: data });
            const json = await res.json();
            if (!res.ok) throw new Error(json.detail || "Something went wrong sending that.");
            setStatus("done");
        } catch (err) {
            setError(err.message);
            setStatus("idle");
        }
    };

    return (
        <>
            <SEO
                title="Identify My Fly — Ask a Trout Start Guide"
                description="Found a mystery fly? Upload a photo and a real Trout Start angler will identify the pattern, tell you what size to reach for, and when to fish it."
            />
            <div data-testid="identify-fly-page">
                <section className="relative pt-44 pb-16 overflow-hidden" data-testid="identify-hero">
                    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
                        <Reveal>
                            <Eyebrow>Ask a Trout Start Guide</Eyebrow>
                            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 leading-[1.05]">
                                Identify <span className="text-teal">my fly.</span>
                            </h1>
                            <p className="mt-6 font-body text-lg text-slate-400 max-w-2xl">
                                Found a mystery pattern in an old fly box? Not sure what insect is hatching on your local river right now?
                                Don't guess. Drop a photo below, and our team will analyze your rig and tell you exactly what it is, what
                                size to reach for, and when to fish it.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15} className="mt-4 lg:mt-0 max-w-md mx-auto w-full lg:max-w-none">
                            <div className="rotate-2 rounded-2xl overflow-hidden border border-white/15 shadow-[0_24px_70px_rgba(0,0,0,0.5)]">
                                <img src="/journal/wetflies-wood-cluster.webp" alt="Hand-tied soft-hackle wet flies on warm wood" className="w-full aspect-square object-cover" />
                            </div>
                        </Reveal>
                    </div>
                </section>

                <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16" data-testid="identify-steps">
                    <div className="grid md:grid-cols-3 gap-6">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.title} delay={i * 0.1}>
                                <div className="h-full rounded-2xl border border-white/10 bg-[#111C2E] p-7 hover:border-teal/40 transition-colors duration-500">
                                    <div className="flex items-center gap-3 text-teal">
                                        <s.icon size={18} />
                                        <span className="font-mono text-[10px] tracking-[0.24em] uppercase">Step {i + 1}</span>
                                    </div>
                                    <h2 className="mt-3 font-serif text-xl sm:text-2xl font-semibold text-slate-50">{s.title}</h2>
                                    <p className="mt-3 font-body text-sm leading-relaxed text-slate-400">{s.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>

                <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-24" data-testid="identify-form-section">
                    <Reveal>
                        {status === "done" ? (
                            <div className="rounded-2xl border border-teal/30 bg-teal/5 p-10 text-center" data-testid="identify-success-panel">
                                <CheckCircle2 size={40} className="mx-auto text-teal" />
                                <h2 className="mt-5 font-serif text-2xl sm:text-3xl font-semibold text-slate-50">Your fly is in the queue.</h2>
                                <p className="mt-4 font-body text-base text-slate-300 max-w-md mx-auto">
                                    A real angler is looking at your photo. Watch your inbox — your full breakdown is on its way,
                                    and you're now on the Field Journal list for the weekly dispatch.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#0D1626] p-7 sm:p-10 space-y-6" data-testid="identify-form">
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="identify-name" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Your Name</label>
                                        <input
                                            id="identify-name"
                                            data-testid="identify-name-input"
                                            type="text"
                                            required
                                            value={form.name}
                                            onChange={set("name")}
                                            className="w-full rounded-xl border border-white/15 bg-[#111C2E] px-4 py-3 font-body text-base text-slate-100 placeholder:text-slate-600 focus:border-teal focus:outline-none transition-colors"
                                            placeholder="Jane Angler"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="identify-email" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Your Email Address</label>
                                        <input
                                            id="identify-email"
                                            data-testid="identify-email-input"
                                            type="email"
                                            required
                                            value={form.email}
                                            onChange={set("email")}
                                            className="w-full rounded-xl border border-white/15 bg-[#111C2E] px-4 py-3 font-body text-base text-slate-100 placeholder:text-slate-600 focus:border-teal focus:outline-none transition-colors"
                                            placeholder="you@example.com"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="identify-water" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Where did you find this fly or what water are you fishing?</label>
                                    <input
                                        id="identify-water"
                                        data-testid="identify-water-input"
                                        type="text"
                                        required
                                        value={form.water}
                                        onChange={set("water")}
                                        className="w-full rounded-xl border border-white/15 bg-[#111C2E] px-4 py-3 font-body text-base text-slate-100 placeholder:text-slate-600 focus:border-teal focus:outline-none transition-colors"
                                        placeholder="Bow River, local shop, inherited box…"
                                    />
                                </div>
                                <div>
                                    <span className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Upload Your Fly Photo</span>
                                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickPhoto} className="hidden" data-testid="identify-photo-input" />
                                    {preview ? (
                                        <div className="relative rounded-xl overflow-hidden border border-white/15">
                                            <img src={preview} alt="Your fly photo" className="w-full max-h-72 object-cover" />
                                            <button
                                                type="button"
                                                onClick={clearPhoto}
                                                data-testid="identify-photo-clear"
                                                className="absolute top-3 right-3 rounded-full bg-[#0B1320]/80 backdrop-blur-md border border-white/15 p-2 text-slate-200 hover:bg-coral hover:border-coral transition-colors"
                                                aria-label="Remove photo"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => fileRef.current?.click()}
                                            data-testid="identify-photo-dropzone"
                                            className="w-full rounded-xl border-2 border-dashed border-white/15 bg-[#111C2E] px-6 py-10 text-center hover:border-teal/50 transition-colors duration-300 group"
                                        >
                                            <ImagePlus size={28} className="mx-auto text-slate-500 group-hover:text-teal transition-colors" />
                                            <span className="mt-3 block font-body text-sm text-slate-400">Tap to choose a photo — JPEG or PNG, close-up works best</span>
                                        </button>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="identify-details" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Any extra details? (e.g., target species, time of year)</label>
                                    <textarea
                                        id="identify-details"
                                        data-testid="identify-details-input"
                                        rows={4}
                                        value={form.details}
                                        onChange={set("details")}
                                        className="w-full rounded-xl border border-white/15 bg-[#111C2E] px-4 py-3 font-body text-base text-slate-100 placeholder:text-slate-600 focus:border-teal focus:outline-none transition-colors resize-none"
                                        placeholder="Caught nothing on it last Tuesday…"
                                    />
                                </div>
                                {error && (
                                    <p className="font-body text-sm text-coral" data-testid="identify-error">{error}</p>
                                )}
                                <button
                                    type="submit"
                                    disabled={status === "sending"}
                                    data-testid="identify-submit-button"
                                    className="w-full rounded-full bg-teal px-6 py-4 font-sans text-sm font-semibold text-[#0B1320] hover:bg-coral hover:text-white transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                                >
                                    {status === "sending" ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" /> Sending to the guides…
                                        </>
                                    ) : (
                                        "Analyze My Fly"
                                    )}
                                </button>
                            </form>
                        )}
                    </Reveal>
                </section>

                <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32" data-testid="identify-why-section">
                    <Reveal>
                        <Eyebrow>Why We Use Real Anglers, Not AI Scanners</Eyebrow>
                        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-100">
                            Tiny hooks don't translate to algorithms.
                        </h2>
                        <p className="mt-5 font-body text-base leading-relaxed text-slate-400">
                            Wrapped feathers and delicate thread wraps don't translate well to automated smartphone algorithms.
                            Blurry riverbank photos constantly break robotic image scanners, leading to frustrating misidentifications.
                        </p>
                        <p className="mt-4 font-body text-base leading-relaxed text-slate-400">
                            At Trout Start, we look at the actual anatomy of your fly — analyzing the hackle, ribbing, and profile —
                            to give you a verified answer you can confidently tie onto your tippet.
                        </p>
                    </Reveal>
                </section>
            </div>
        </>
    );
}
